import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from './config';
// What a saved message keeps (Firestore rejects `undefined`), a quote included.
import { cleanMessage } from '../utils/savedMessage';

// Videos collection functions
// NOTE: prefer getVideoIndex() from ./catalog — these fetch full transcripts.
export const getVideos = async () => {
  try {
    const videosRef = collection(db, 'videos');
    const q = query(videosRef, orderBy('publishDate', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching videos:', error);
    throw error;
  }
};

export const getVideo = async (videoId) => {
  try {
    const videoRef = doc(db, 'videos', videoId);
    const videoDoc = await getDoc(videoRef);
    if (videoDoc.exists()) {
      return { id: videoDoc.id, ...videoDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching video:', error);
    throw error;
  }
};

// Chat functions
export const createChat = async (userId, videoIds, initialMessage, chatType = 'select') => {
  try {
    const chatRef = collection(db, 'users', userId, 'chats');
    const newChat = {
      videoIds: videoIds,
      chatType: chatType, // 'select' or 'collections'
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      messages: [{
        role: 'user',
        content: initialMessage,
        timestamp: new Date().toISOString()
      }]
    };
    const docRef = await addDoc(chatRef, newChat);
    return docRef.id;
  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
};

export const getChats = async (userId) => {
  try {
    const chatsRef = collection(db, 'users', userId, 'chats');
    const q = query(chatsRef, orderBy('lastUpdated', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching chats:', error);
    throw error;
  }
};

export const getChat = async (userId, chatId) => {
  try {
    const chatRef = doc(db, 'users', userId, 'chats', chatId);
    const chatDoc = await getDoc(chatRef);
    if (chatDoc.exists()) {
      return { id: chatDoc.id, ...chatDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching chat:', error);
    throw error;
  }
};

export const updateChat = async (userId, chatId, message) => {
  try {
    const chatRef = doc(db, 'users', userId, 'chats', chatId);
    await updateDoc(chatRef, {
      messages: arrayUnion({
        role: message.role,
        content: message.content,
        timestamp: new Date().toISOString()
      }),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating chat:', error);
    throw error;
  }
};

/**
 * Opt-in save: writes the full conversation so far to the user's library.
 * Saving is opt-in: the library list only holds a conversation once the user
 * chooses to save it; after that, the server appends subsequent messages.
 * (The team's private copy of every conversation is separate: conversations.js.)
 */
export const saveChatWithMessages = async (userId, chatId, videoIds, messages, chatType = 'select') => {
  const chatRef = doc(db, 'users', userId, 'chats', chatId);
  // The cards in a conversation (crisis, account nudge, donation) are the
  // app's, not the conversation's: saved, one would come back as an empty answer.
  const cleaned = (messages || [])
    .filter((m) => !m.local && !m.safetyCard && !m.nudge && !m.giving && (m.role === 'user' || m.role === 'assistant'))
    .map(cleanMessage);
  const firstUser = cleaned.find((m) => m.role === 'user');
  const title = (firstUser?.content || 'Conversation').slice(0, 80);
  await setDoc(chatRef, {
    videoIds: videoIds || [],
    chatType,
    title,
    createdAt: serverTimestamp(),
    lastUpdated: serverTimestamp(),
    messages: cleaned,
  }, { merge: true });
};

export const deleteChat = async (userId, chatId) => {
  try {
    const chatRef = doc(db, 'users', userId, 'chats', chatId);
    await deleteDoc(chatRef);
  } catch (error) {
    console.error('Error deleting chat:', error);
    throw error;
  }
};

export const upsertUserProfile = async (userId, { displayName, email }) => {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, {
    ...(displayName != null ? { displayName: String(displayName).trim() } : {}),
    ...(email != null ? { email } : {}),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const getUserProfile = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};
